/**
 * PLA-67: useAnalysisFrames Hook
 * 
 * Fetches extracted frames for an analysis run.
 * Returns a map of fault_id → frame data for easy lookup.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AnalysisFrame {
  id: string;
  analysis_run_id: string;
  fault_id: string;
  frame_index: number;
  fps: number;
  timestamp_ms: number;
  image_url: string;
  created_at: string;
}

export interface AnalysisFramesMap {
  [fault_id: string]: {
    image_url: string;
    frame_index: number;
    timestamp_ms: number;
  };
}

function parseStorageRef(raw: string): { bucket: string; path: string } | null {
  const v = (raw || '').trim();
  if (!v) return null;

  // Format 1: bucket:path
  const m = v.match(/^([a-z0-9-]+):(.*)$/i);
  if (m && m[1] && m[2]) {
    return { bucket: m[1], path: m[2].replace(/^\/+/, '') };
  }

  // Format 2: public URL (may 404 for private buckets, but is parseable)
  // https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path>
  const publicMarker = '/storage/v1/object/public/';
  const idx = v.indexOf(publicMarker);
  if (idx !== -1) {
    const rest = v.slice(idx + publicMarker.length);
    const [bucket, ...pathParts] = rest.split('/');
    const path = pathParts.join('/');
    if (bucket && path) return { bucket, path };
  }

  // Format 3: bare storage path (assume swing-lab)
  if (!v.startsWith('http') && v.includes('/')) {
    return { bucket: 'swing-lab', path: v.replace(/^\/+/, '') };
  }

  return null;
}

export function useAnalysisFrames(runId: string | null | undefined) {
  const [frames, setFrames] = useState<AnalysisFramesMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setFrames({});
      setLoading(false);
      setError(null);
      return;
    }

    async function fetchFrames() {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('analysis_frames')
          .select('*')
          .eq('analysis_run_id', runId)
          .order('frame_index');

        if (fetchError) {
          throw fetchError;
        }

        // Convert array to map: { [fault_id]: frame_data }
        const framesMap: AnalysisFramesMap = {};
        if (data) {
          // Resolve signed URLs for storage-backed images (private buckets)
          await Promise.all(
            data.map(async (frame) => {
              const rawUrl: string = frame.image_url;
              const ref = parseStorageRef(rawUrl);
              let resolvedUrl = rawUrl;

              if (ref) {
                const { data: signed } = await supabase.storage
                  .from(ref.bucket)
                  .createSignedUrl(ref.path, 60 * 60); // 1 hour
                if (signed?.signedUrl) resolvedUrl = signed.signedUrl;
              }

              framesMap[frame.fault_id] = {
                image_url: resolvedUrl,
                frame_index: frame.frame_index,
                timestamp_ms: frame.timestamp_ms,
              };
            })
          );
        }

        setFrames(framesMap);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error('[useAnalysisFrames] Error fetching frames', {
          runId,
          error: errorMessage
        });
        setError(errorMessage);
        setFrames({});
      } finally {
        setLoading(false);
      }
    }

    fetchFrames();
  }, [runId]);

  return {
    frames,
    loading,
    error,
    hasFrames: Object.keys(frames).length > 0,
  };
}

